import { useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUpload,
    faFileCsv,
    faDownload
} from '@fortawesome/free-solid-svg-icons';
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";

import { downloadSample } from "features/admin/adminApi";

interface Props {
    show: boolean;
    onHide: () => void;

    title: string;

    entityName: string;

    sampleType: "locum" | "pharmacy";

    validateApi: (file: File) => Promise<any>;

    importApi: (records: any[]) => Promise<any>;

    onSuccess?: () => void;
}

const BulkUploadModal = ({
    show,
    onHide,
    title,
    entityName,
    sampleType,
    validateApi,
    importApi,
    onSuccess
}: Props) => {
    const [file, setFile] = useState<File | null>(null);
    const [validationResult, setValidationResult] = useState<any>(null);
    const [uploading, setUploading] = useState(false);
    const [importing, setImporting] = useState(false);

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!e.target.files?.length) return;

        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) return;

        try {
            setUploading(true);

            const result = await validateApi(file);

            setValidationResult(result);

        } catch (err: any) {
            alert(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleImport = async () => {
        try {
            setImporting(true);

            const response = await importApi(
                validationResult.validRecords
            );

            toast.success(
                `${response.imported} ${entityName} imported successfully`
            );

            onSuccess?.();

            closeModal();

        } catch (err: any) {

            toast.error(
                err.message || `Failed to import ${entityName}`
            );

            console.error(err);

        } finally {

            setImporting(false);

        }
    };

    const closeModal = () => {
        setFile(null);
        setValidationResult(null);
        onHide();
    };

    const handleDownloadSample = async () => {
        try {

            const blob = await downloadSample(sampleType);

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = url;

            link.download =
                sampleType === "locum"
                    ? "locum_sample.xlsx"
                    : "pharmacy_sample.xlsx";

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error(err);
            alert("Failed to download sample.");
        }
    };

    return (
        <Modal
            show={show}
            onHide={closeModal}
            centered
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>

                    <FontAwesomeIcon
                        icon={faFileCsv}
                        className="me-2 text-primary"
                    />

                    {title}

                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {importing ? (
                    <Alert variant="warning" className="mt-3 mb-0 p-3 mb-3">
                        <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                        />
                        Importing {entityName.toLowerCase()}. Please don't close this window...
                    </Alert>
                ) : (
                    <Alert variant="info">
                        Upload an Excel file.
                        Validate the records before importing them.
                    </Alert>
                )}

                <div className="mb-4">

                    <Button
                        variant="phoenix-secondary"
                        startIcon={<FontAwesomeIcon icon={faDownload} />}
                        onClick={handleDownloadSample}
                    >
                        Download Sample
                    </Button>

                </div>

                <Form.Group>

                    <Form.Label>

                        Select Excel File

                    </Form.Label>

                    <Form.Control
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                    />

                </Form.Group>

                {file && (
                    <div className="mt-3">

                        Selected File :

                        <strong className="ms-2">
                            {file.name}
                        </strong>

                    </div>
                )}
                {validationResult && (

                    <div className="mt-4">

                        <hr />

                        <h5 className="mb-3">
                            Validation Summary
                        </h5>

                        <div className="row text-center">

                            <div className="col-md-4">

                                <div className="border rounded p-3">

                                    <h3 className="text-primary">
                                        {validationResult.total}
                                    </h3>

                                    <small>Total</small>

                                </div>

                            </div>

                            <div className="col-md-4">

                                <div className="border rounded p-3">

                                    <h3 className="text-success">
                                        {validationResult.valid}
                                    </h3>

                                    <small>Valid</small>

                                </div>

                            </div>

                            <div className="col-md-4">

                                <div className="border rounded p-3">

                                    <h3 className="text-danger">
                                        {validationResult.invalid}
                                    </h3>

                                    <small>Invalid</small>

                                </div>

                            </div>

                        </div>

                    </div>

                )}
                {validationResult?.errors?.length > 0 && (

                    <>

                        <h5 className="mt-4">
                            Validation Errors
                        </h5>

                        <div className="table-responsive">

                            <table className="table table-bordered align-middle">

                                <thead>

                                    <tr>

                                        <th>Row</th>

                                        <th>Email</th>

                                        <th>Error</th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {validationResult.errors.map(
                                        (error: any, index: number) => (

                                            <tr key={index}>

                                                <td>{error.row}</td>

                                                <td>{error.email}</td>

                                                <td className="text-danger">
                                                    {error.reason}
                                                </td>

                                            </tr>

                                        ))
                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )}

            </Modal.Body>
            <Modal.Footer>

                <Button
                    variant="phoenix-secondary"
                    onClick={closeModal}
                    disabled={uploading || importing}
                >
                    Cancel
                </Button>

                {!validationResult && (

                    <Button
                        variant="phoenix-primary"
                        loading={uploading}
                        disabled={!file || importing}
                        onClick={handleUpload}
                        startIcon={<FontAwesomeIcon icon={faUpload} />}
                    >
                        Upload & Validate
                    </Button>

                )}

                {validationResult && validationResult.valid > 0 && (

                    <Button
                        variant="phoenix-success"
                        onClick={handleImport}
                        disabled={importing}
                    >
                        {importing ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Importing...
                            </>
                        ) : (
                            `Import ${validationResult.valid} ${entityName}`
                        )}
                    </Button>

                )}

            </Modal.Footer>

        </Modal>
    );
};

export default BulkUploadModal;